import React, { memo, useContext, useEffect, useState } from "react";
import Modal from "~/shared/ui/Fields/Modal";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { useTranslation } from "react-i18next";
import s from "../CameraZonesView.less";
import CameraForm, { TCameraFormType } from "~/components/Forms/CameraForm";
import { useCurrentDealerInfo } from "~/hooks/useCurrentDealerInfo";
import {
  Camera,
  useAddNotificationMutation,
  useEditCameraMutation,
  useGetCameraByIdLazyQuery,
  useSetCameraMutation,
} from "~/schemas/generated/graphql";
import { NOTIFICATIONS } from "~/constans/notifications";
import Preloader from "~/components/Preloader";
import { get } from "lodash";
import { CamerasContext } from "~/pages/CameraZonesView";
import { useParams } from "react-router-dom";

type TParameters = {
  openModal: string;
  currentId: string;
};

const CameraModal = () => {
  // Utils
  const { t } = useTranslation();
  const { openModal, currentId } = useURLParamsGetter<TParameters>();
  const setURLParams = useURLParamsSetter();
  const { fetchCameras, fetchZonesHandler } = useContext(CamerasContext);
  const params = useParams<{ id: string }>();
  const { id } = params;
  const handleCloseModal = () =>
    setURLParams({ openModal: undefined, currentId: undefined });

  const [dealerData] = useCurrentDealerInfo();

  // State
  const [isOpen, setIsOpen] = useState(
    openModal === "Create" || openModal === "Edit"
  );
  const [cameraInstance, setCameraInstance] = useState<Camera>();

  // Querys
  const [getCameraInstance, getCameraInstanceState] = useGetCameraByIdLazyQuery(
    {
      fetchPolicy: "no-cache",
    }
  );

  // Mutations
  const [addNotif] = useAddNotificationMutation();
  const [setCamera] = useSetCameraMutation({
    onCompleted: () => {
      addNotif({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("cameraView.createCameraSuccess"),
        },
      });
      id ? fetchCameras() : fetchZonesHandler();
      handleCloseModal();
    },
  });
  const [editCamera] = useEditCameraMutation({
    onCompleted: () => {
      addNotif({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("cameraView.editCameraSuccess"),
        },
      });
      id ? fetchCameras() : fetchZonesHandler();
      handleCloseModal();
    },
  });

  // Handlers
  const title = currentId
    ? `${t("cameraView.modalEdit")}: ${
        dealerData ? dealerData.DealerName : "..."
      }`
    : `${t("cameraView.modalCreate")} ${
        dealerData ? dealerData.DealerName : "..."
      }`;

  const onSubmit = (data: TCameraFormType) => {
    const mutationData = {
      variables: {
        ...(currentId ? { id: currentId } : {}),
        ...data,
        cameraZoneId: data.cameraZoneId.value,
        dealerId: dealerData.id,
      },
    };
    if (currentId) {
      editCamera(mutationData);
    } else {
      setCamera(mutationData);
    }
  };

  useEffect(() => {
    if (currentId) {
      getCameraInstance({ variables: { id: currentId } });
    }
  }, [currentId]);

  useEffect(() => {
    if (getCameraInstanceState.data) {
      setCameraInstance(
        get(getCameraInstanceState.data, "CameraGetter.getCamera")
      );
    }
  }, [getCameraInstanceState.data]);

  useEffect(() => {
    setIsOpen(openModal === "Create" || openModal === "Edit");
  }, [openModal]);

  useEffect(() => {
    if (!isOpen && cameraInstance) setCameraInstance(undefined);
  }, [isOpen]);

  if (getCameraInstanceState.loading) return <Preloader />;

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      actionClose={handleCloseModal}
      customClassName={s.modal}
    >
      <CameraForm submitHandler={onSubmit} cameraInstance={cameraInstance} />
    </Modal>
  );
};

export default memo(CameraModal);
